import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Switch,
    TextField,
    Typography,
} from '@mui/material';
import { ArrowLeft, Camera, Download, History, ScanLine } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { standaloneFieldSx } from '../../common/fieldStyles';
import { httpClient } from '../../common/httpclient';
import DialogForm from '../../component/dialog/DialogForm';
import PageScaffold from '../../component/pageScaffold/PageScaffold';
import DefaultTable from '../../component/table/DefaultTable';
import BarcodeScanner from '../../component/barcode/BarcodeScanner';
import { APP_BAR_OFFSET } from '../../component/layout/mobileShell';
import usePermissions from '../../hooks/usePermissions';
import useIsMobileShell from '../../hooks/useIsMobileShell';
import MobileInventoryDetail from '../../component/inventory/MobileInventoryDetail';
import { useAppBarTitle } from '../../component/layout/AppBarActions';
import { INVENTORY_STATUS, inventoryErrorMessage } from './inventoryStatus';

const SUMMARY_COLLAPSED_KEY = 'inventory.summaryCollapsed';
const KIND_LABELS ={ scan: 'Scan', manual: 'Contagem', recount: 'Recontagem' };

function formatDateTime(value) {
    if (!value) return '—';
    return new Date(value).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

function SummaryCard({ label, value, color }) {
    return (
        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: '10px', px: 1.25, py: 0.75, minWidth: 0, bgcolor: 'background.paper' }}>
            <Typography noWrap title={label} sx={{ fontSize: '10.5px', fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '.04em', lineHeight: 1.3 }}>
                {label}
            </Typography>
            <Typography noWrap title={String(value)} sx={{ fontSize: '16px', fontWeight: 800, lineHeight: 1.3, fontVariantNumeric: 'tabular-nums', color: color || 'text.primary' }}>{value}</Typography>
        </Box>
    );
}

const MOBILE_PAGE_SIZE = 30;
const MOBILE_FILTERS = [
    { key: 'all', label: 'Todos' },
    { key: 'pending', label: 'Pendentes' },
    { key: 'counted', label: 'Contados' },
    { key: 'divergent', label: 'Divergentes' },
];

// Lista compacta de itens para o mobile (substitui os cards do DefaultTable).
function MobileItemList({ loading, allItems, canCountItems, edits, setEdits, inputRefs, commitCount, diffColor, openHistory }) {
    const [filter, setFilter] = useState('all');
    const [limit, setLimit] = useState(MOBILE_PAGE_SIZE);
    const [expanded, setExpanded] = useState(null);

    const isCounted = (item) => item.counted_qty !== null && item.counted_qty !== undefined;
    const isDivergent = (item) => item.difference !== null && item.difference !== undefined && item.difference !== 0;
    const counts = {
        all: allItems.length,
        pending: allItems.filter((i) => !isCounted(i)).length,
        counted: allItems.filter(isCounted).length,
        divergent: allItems.filter(isDivergent).length,
    };

    const filtered = allItems
        .filter((i) => (filter === 'pending' ? !isCounted(i) : filter === 'counted' ? isCounted(i) : filter === 'divergent' ? isDivergent(i) : true))
        .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'pt-BR'));
    const visible = filtered.slice(0, limit);

    const barColor = (item) => {
        if (!isCounted(item)) return 'divider';
        return isDivergent(item) ? (item.difference > 0 ? 'success.main' : 'error.main') : 'primary.main';
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', gap: 0.75, overflowX: 'auto', pb: 0.75, mb: 0.5 }}>
                {MOBILE_FILTERS.map((f) => (
                    <Chip
                        key={f.key}
                        size="small"
                        label={`${f.label} ${counts[f.key]}`}
                        onClick={() => { setFilter(f.key); setLimit(MOBILE_PAGE_SIZE); }}
                        color={filter === f.key ? 'primary' : 'default'}
                        variant={filter === f.key ? 'filled' : 'outlined'}
                        sx={{ fontWeight: 700, fontSize: '12px', height: 30, flexShrink: 0 }}
                    />
                ))}
            </Box>

            {loading && <Typography sx={{ fontSize: '13px', color: 'text.secondary', py: 2 }}>Carregando...</Typography>}
            {!loading && filtered.length === 0 && (
                <Typography sx={{ fontSize: '13px', color: 'text.secondary', py: 2, textAlign: 'center' }}>Nenhum produto encontrado.</Typography>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                {visible.map((item) => {
                    const open = expanded === item.id;
                    const diffText = item.difference === null || item.difference === undefined ? '—' : (item.difference > 0 ? `+${item.difference}` : item.difference);
                    return (
                        <Box
                            key={item.id}
                            onClick={() => setExpanded(open ? null : item.id)}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                bgcolor: 'background.paper',
                                border: 1,
                                borderColor: 'divider',
                                borderLeft: 4,
                                borderLeftColor: barColor(item),
                                borderRadius: '8px',
                                px: 1.25,
                                py: 0.75,
                            }}
                        >
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography noWrap sx={{ fontSize: '14px', fontWeight: 700, lineHeight: 1.3 }}>{item.name}</Typography>
                                <Typography noWrap sx={{ fontSize: '11.5px', color: 'text.secondary', lineHeight: 1.3 }}>
                                    {item.code} · Sist. {item.expected_qty} · Dif.{' '}
                                    <Box component="span" sx={{ fontWeight: 700, color: item.difference === null || item.difference === undefined ? 'text.disabled' : diffColor(item.difference) }}>{diffText}</Box>
                                </Typography>
                                {open && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.5, flexWrap: 'wrap' }}>
                                        <Typography sx={{ fontSize: '11.5px', color: 'text.secondary' }}>Custo: R$ {item.cost}</Typography>
                                        <Typography sx={{ fontSize: '11.5px', color: 'text.secondary' }}>
                                            Impacto: {item.difference === null || item.difference === undefined ? '—' : `R$ ${item.impact}`}
                                        </Typography>
                                        <Button
                                            size="small"
                                            startIcon={<History size={14} />}
                                            onClick={(e) => { e.stopPropagation(); openHistory(item); }}
                                            sx={{ minHeight: 32, fontSize: '12px', p: 0.5 }}
                                        >
                                            Histórico
                                        </Button>
                                    </Box>
                                )}
                            </Box>
                            {canCountItems ? (
                                <TextField
                                    size="small"
                                    type="number"
                                    placeholder="0"
                                    inputRef={(el) => { inputRefs.current[item.id] = el; }}
                                    inputProps={{ min: 0, inputMode: 'numeric', 'aria-label': `Quantidade contada de ${item.name}`, style: { fontSize: '16px', textAlign: 'center', padding: '0 4px', height: 44, boxSizing: 'border-box' } }}
                                    value={edits[item.id] !== undefined ? edits[item.id] : (item.counted_qty ?? '')}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={(e) => setEdits((prev) => ({ ...prev, [item.id]: e.target.value }))}
                                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); } }}
                                    onBlur={() => commitCount(item)}
                                    sx={{ width: 76, flexShrink: 0 }}
                                />
                            ) : (
                                <Typography sx={{ fontSize: '16px', fontWeight: 800, flexShrink: 0 }}>{item.counted_qty ?? '—'}</Typography>
                            )}
                        </Box>
                    );
                })}
            </Box>

            {filtered.length > limit && (
                <Button fullWidth onClick={() => setLimit((l) => l + MOBILE_PAGE_SIZE)} sx={{ mt: 1, minHeight: 44 }}>
                    Mostrar mais ({filtered.length - limit} restantes)
                </Button>
            )}
        </Box>
    );
}

export default function View() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { hasPermission } = usePermissions();

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [onlyDivergent, setOnlyDivergent] = useState(false);

    const [scanCode, setScanCode] = useState('');
    const [lastScan, setLastScan] = useState(null);
    const scanRef = useRef(null);
    const [scannerOpen, setScannerOpen] = useState(false);
    const [scanFeedback, setScanFeedback] = useState(null);
    const isMobile = useIsMobileShell();

    // Quantidades digitadas e ainda não gravadas, por item.
    const [edits, setEdits] = useState({});
    const inputRefs = useRef({});
    const [recountItem, setRecountItem] = useState(null); // { item, diff }
    const [recountMode, setRecountMode] = useState({}); // itemId -> true quando o usuário pediu recontagem

    const [confirmAction, setConfirmAction] = useState(null); // 'start' | 'submit' | 'approve' | 'cancel'
    const [historyItem, setHistoryItem] = useState(null);
    const [history, setHistory] = useState([]);

    // Altura dos cards indicadores: null = automática (expandido), 0 = recolhido, número = arrastando.
    const [summaryHeight, setSummaryHeight] = useState(() => {
        try {
            return sessionStorage.getItem(SUMMARY_COLLAPSED_KEY) === '1' ? 0 : null;
        } catch (err) {
            return null;
        }
    });
    // Detalhes do cabeçalho (descrição/auditoria): recolhem depois dos cards, com a mesma semântica.
    const [extraHeight, setExtraHeight] = useState(() => {
        try {
            return sessionStorage.getItem(SUMMARY_COLLAPSED_KEY) === '1' ? 0 : null;
        } catch (err) {
            return null;
        }
    });
    const [summaryDragging, setSummaryDragging] = useState(false);
    const summaryRef = useRef(null);
    const extraRef = useRef(null);
    const summaryDrag = useRef(null);

    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');

    const notify = (message, severity = 'success') => {
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
        setOpenSnackbar(true);
    };

    const fetchData = useCallback(async () => {
        try {
            const response = await httpClient.get(`/inventories/${id}`);
            setData(response.data);
        } catch (error) {
            console.error('Error fetching inventory:', error);
            notify(inventoryErrorMessage(error, 'Erro ao carregar inventário.'), 'error');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    useEffect(() => {
        fetchData().finally(() => setLoading(false));
    }, [fetchData]);

    const inventory = data?.inventory;
    const status = inventory?.status;
    const isCounting = status === 'counting';
    const canCountItems = status === 'counting' || status === 'review';

    // Mantém o foco no campo de scan durante a contagem.
    useEffect(() => {
        if (isCounting && scanRef.current) scanRef.current.focus();
    }, [isCounting, loading]);

    const handleScan = async (scannedCode) => {
        const code = (typeof scannedCode === 'string' ? scannedCode : scanCode).trim();
        if (!code) return;
        setScanCode('');
        try {
            const response = await httpClient.post(`/inventories/${id}/scan`, { code });
            setLastScan(response.data);
            setScanFeedback({ type: 'success', code, name: response.data?.name, qty: response.data?.counted_qty });
            await fetchData();
        } catch (error) {
            setLastScan(null);
            const notFound = error?.response?.status === 404;
            const message = notFound ? `Produto com código "${code}" não encontrado.` : inventoryErrorMessage(error, 'Erro ao registrar scan.');
            setScanFeedback({ type: 'error', code, message });
            notify(message, 'error');
        } finally {
            // Com a câmera (código recebido por parâmetro) não foca o campo, para não abrir o teclado.
            if (typeof scannedCode !== 'string' && scanRef.current) scanRef.current.focus();
        }
    };

    const handleOpenCamera = () => {
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
            notify('Câmera indisponível neste navegador. A leitura exige HTTPS e permissão de câmera. Digite o código do produto.', 'warning');
            return;
        }
        setScanFeedback(null);
        setScannerOpen(true);
    };

    const commitCount = async (item) => {
        const raw = edits[item.id];
        if (raw === undefined || raw === '') return;
        const quantity = parseInt(raw, 10);
        if (Number.isNaN(quantity) || quantity < 0) {
            notify('Informe uma quantidade válida.', 'error');
            return;
        }
        setEdits((prev) => {
            const next = { ...prev };
            delete next[item.id];
            return next;
        });
        if (item.counted_qty === quantity && !recountMode[item.id]) return;

        const reason = recountMode[item.id]
            ? 'Recontagem'
            : (status === 'review' ? 'Alteração na conferência' : '');
        try {
            await httpClient.put(`/inventories/${id}/items/${item.id}/count`, { quantity, reason });
            setRecountMode((prev) => {
                const next = { ...prev };
                delete next[item.id];
                return next;
            });
            await fetchData();
            const diff = quantity - item.expected_qty;
            if (diff !== 0) setRecountItem({ item, diff });
        } catch (error) {
            notify(inventoryErrorMessage(error, 'Erro ao registrar contagem.'), 'error');
        }
    };

    const handleRecount = () => {
        const item = recountItem?.item;
        setRecountItem(null);
        if (!item) return;
        setRecountMode((prev) => ({ ...prev, [item.id]: true }));
        setEdits((prev) => ({ ...prev, [item.id]: '' }));
        setTimeout(() => inputRefs.current[item.id]?.focus(), 50);
    };

    const openHistory = async (item) => {
        setHistoryItem(item);
        setHistory([]);
        try {
            const response = await httpClient.get(`/inventories/${id}/items/${item.id}/counts`);
            setHistory(Array.isArray(response.data) ? response.data : []);
        } catch (error) {
            notify(inventoryErrorMessage(error, 'Erro ao carregar histórico.'), 'error');
        }
    };

    const ACTIONS = {
        start: { path: 'start', success: 'Contagem iniciada.', title: 'Iniciar contagem', content: 'Ao iniciar, o estoque atual de cada produto é registrado como base da contagem. Movimentações durante o inventário continuam permitidas e serão consideradas no fechamento.', button: 'Iniciar' },
        submit: { path: 'submit', success: 'Inventário enviado para conferência.', title: 'Enviar para conferência', content: 'Produtos não contados não terão ajuste de estoque. Deseja enviar o inventário para conferência?', button: 'Enviar' },
        approve: { path: 'approve', success: 'Inventário aprovado e estoque ajustado.', title: 'Aprovar e ajustar estoque', content: 'Serão gerados os movimentos de ajuste de todas as diferenças (contado menos estoque esperado), com origem neste inventário. Esta ação não pode ser desfeita.', button: 'Aprovar e ajustar' },
        cancel: { path: 'cancel', success: 'Inventário cancelado.', title: 'Cancelar inventário', content: 'O estoque não será alterado. Deseja cancelar este inventário?', button: 'Cancelar inventário' },
    };

    const runAction = async () => {
        const action = ACTIONS[confirmAction];
        if (!action) return;
        try {
            setBusy(true);
            await httpClient.post(`/inventories/${id}/${action.path}`);
            setConfirmAction(null);
            await fetchData();
            notify(action.success);
        } catch (error) {
            setConfirmAction(null);
            notify(inventoryErrorMessage(error, 'Erro ao executar a ação.'), 'error');
        } finally {
            setBusy(false);
        }
    };

    const handleExport = async (format) => {
        try {
            await httpClient.downloadFile(`/inventories/${id}/report?format=${format}`, {
                title: `inventario_${inventory.code}`,
                extension: format,
            });
        } catch (error) {
            console.error('Error exporting inventory:', error);
            notify('Erro ao exportar relatório.', 'error');
        }
    };

    const items = (data?.items || []).filter((item) => !onlyDivergent || (item.difference !== null && item.difference !== 0));
    const summary = data?.summary;

    const diffColor = (diff) => (diff > 0 ? 'success.main' : diff < 0 ? 'error.main' : 'text.secondary');

    const columns = [
        { path: 'code', name: 'Código', invisible: false, render: (row) => <Typography sx={{ fontSize: '13px', fontWeight: 700 }}>{row.code}</Typography> },
        { path: 'name', name: 'Produto', invisible: false, render: (row) => <Typography sx={{ fontSize: '13px' }}>{row.name}</Typography> },
        { path: 'expected_qty', name: 'Sistema', invisible: false, render: (row) => <Typography sx={{ fontSize: '13px' }}>{row.expected_qty}</Typography> },
        {
            path: 'counted_qty',
            name: 'Contado',
            invisible: false,
            render: (row) => (canCountItems ? (
                <TextField
                    size="small"
                    type="number"
                    inputRef={(el) => { inputRefs.current[row.id] = el; }}
                    inputProps={{ min: 0, style: { fontSize: '13px', width: 70 } }}
                    value={edits[row.id] !== undefined ? edits[row.id] : (row.counted_qty ?? '')}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => setEdits((prev) => ({ ...prev, [row.id]: e.target.value }))}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') { e.preventDefault(); commitCount(row); return; }
                        if (e.key === 'Tab') {
                            // Tab/Shift+Tab pulam para o "Contado" da próxima/anterior linha montada;
                            // sem alvo, mantém o comportamento padrão do navegador.
                            const index = items.findIndex((item) => item.id === row.id);
                            const target = index >= 0 ? items[index + (e.shiftKey ? -1 : 1)] : null;
                            const targetInput = target ? inputRefs.current[target.id] : null;
                            if (targetInput) {
                                e.preventDefault();
                                targetInput.focus();
                                targetInput.select();
                            }
                        }
                    }}
                    onBlur={() => commitCount(row)}
                />
            ) : (
                <Typography sx={{ fontSize: '13px' }}>{row.counted_qty ?? '—'}</Typography>
            )),
        },
        {
            path: 'difference',
            name: 'Diferença',
            invisible: false,
            render: (row) => (
                <Typography sx={{ fontSize: '13px', fontWeight: 700, color: row.difference === null ? 'text.disabled' : diffColor(row.difference) }}>
                    {row.difference === null ? '—' : (row.difference > 0 ? `+${row.difference}` : row.difference)}
                </Typography>
            ),
        },
        { path: 'cost', name: 'Custo (R$)', invisible: false, render: (row) => <Typography sx={{ fontSize: '13px' }}>{row.cost}</Typography> },
        {
            path: 'impact',
            name: 'Impacto (R$)',
            invisible: false,
            render: (row) => (
                <Typography sx={{ fontSize: '13px', fontWeight: 700, color: row.difference === null ? 'text.disabled' : diffColor(row.difference) }}>
                    {row.difference === null ? '—' : row.impact}
                </Typography>
            ),
        },
    ];

    const tableActions = [{
        icon: <History size={16} strokeWidth={2} />,
        props: {
            onClick: (row) => openHistory(row),
            tooltip: 'Histórico de contagens',
            style: { cursor: 'pointer' },
        },
    }];

    const statusInfo = INVENTORY_STATUS[status] || INVENTORY_STATUS.draft;
    const canEdit = hasPermission('inventory.update');
    const canApprove = hasPermission('inventory.approve');
    const canExport = hasPermission('report.inventory');
    const action = ACTIONS[confirmAction];
    const canCollapseSummary = Boolean(summary) && isCounting && canEdit;

    // Arrastar o divisor para cima recolhe os cards indicadores; para baixo expande.
    const handleSummaryDragStart = (e) => {
        const el = summaryRef.current;
        const extra = extraRef.current;
        if (!el || !extra) return;
        e.currentTarget.setPointerCapture?.(e.pointerId);
        const fullS = el.scrollHeight;
        const fullE = extra.scrollHeight;
        const startT = (fullS - el.offsetHeight) + (fullE - extra.offsetHeight);
        summaryDrag.current = { startY: e.clientY, startT, fullS, fullE, moved: false, current: startT };
        applyCollapse(startT, fullS, fullE);
        setSummaryDragging(true);
    };

    // t = quanto já foi recolhido (px): primeiro os cards, depois os detalhes do cabeçalho.
    const applyCollapse = (t, fullS, fullE) => {
        setSummaryHeight(fullS - Math.min(t, fullS));
        setExtraHeight(fullE - Math.max(0, t - fullS));
    };

    const handleSummaryDragMove = (e) => {
        const drag = summaryDrag.current;
        if (!drag) return;
        const dy = e.clientY - drag.startY;
        if (Math.abs(dy) > 3) drag.moved = true;
        drag.current = Math.min(drag.fullS + drag.fullE, Math.max(0, drag.startT - dy));
        applyCollapse(drag.current, drag.fullS, drag.fullE);
    };

    const handleSummaryDragEnd = (e) => {
        const drag = summaryDrag.current;
        if (!drag) return;
        e.currentTarget.releasePointerCapture?.(e.pointerId);
        summaryDrag.current = null;
        setSummaryDragging(false);
        // Toque sem arrastar alterna; arrastando, faz snap para o lado mais próximo.
        const collapse = drag.moved ? drag.current > (drag.fullS + drag.fullE) / 2 : drag.startT === 0;
        setSummaryHeight(collapse ? 0 : null);
        setExtraHeight(collapse ? 0 : null);
        try {
            sessionStorage.setItem(SUMMARY_COLLAPSED_KEY, collapse ? '1' : '0');
        } catch (err) { /* storage indisponível */ }
    };

    useAppBarTitle(isMobile && inventory ? `Inventário #${inventory.code}` : '');

    // "Salvar parcial" (mobile): grava as contagens digitadas e ainda não gravadas (mesmo fluxo do blur).
    const saveAllEdits = async () => {
        if (document.activeElement && document.activeElement.blur) document.activeElement.blur();
        const pending = (data?.items || []).filter((item) => edits[item.id] !== undefined && edits[item.id] !== '');
        for (const item of pending) {
            // eslint-disable-next-line no-await-in-loop
            await commitCount(item);
        }
        notify('Contagem salva.');
    };

    const dialogs = (
        <>
            <DialogForm
                iconType="warning"
                open={Boolean(confirmAction)}
                title={action?.title || ''}
                confirmButtonText={action?.button || 'Confirmar'}
                cancelButtonText="Voltar"
                content={action?.content || ''}
                onSubmit={runAction}
                onClose={() => setConfirmAction(null)}
                loading={busy}
                loadingText="Processando..."
            />

            <DialogForm
                iconType="warning"
                open={Boolean(recountItem)}
                title="Divergência encontrada"
                confirmButtonText="Recontar"
                cancelButtonText="Manter contagem"
                content={recountItem ? `Existe uma divergência de ${Math.abs(recountItem.diff)} unidade${Math.abs(recountItem.diff) === 1 ? '' : 's'} em "${recountItem.item.name}". Deseja realizar uma recontagem?` : ''}
                onSubmit={handleRecount}
                onClose={() => setRecountItem(null)}
            />

            <Dialog open={Boolean(historyItem)} onClose={() => setHistoryItem(null)} maxWidth="sm" fullWidth>
                <DialogTitle>Contagens — {historyItem?.name}</DialogTitle>
                <DialogContent>
                    {history.length === 0 && (
                        <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>Nenhuma contagem registrada.</Typography>
                    )}
                    {history.map((c) => (
                        <Box key={c.id} sx={{ py: 1, borderBottom: 1, borderColor: 'divider' }}>
                            <Typography sx={{ fontSize: '13px', fontWeight: 700 }}>
                                {c.seq}ª · {KIND_LABELS[c.kind] || c.kind}: {c.quantity}
                                {c.previous_qty !== null && c.previous_qty !== undefined ? ` (anterior: ${c.previous_qty})` : ''}
                            </Typography>
                            <Typography sx={{ fontSize: '11.5px', color: 'text.secondary' }}>
                                {c.user || '—'} · {formatDateTime(c.created_at)}{c.reason ? ` · ${c.reason}` : ''}
                            </Typography>
                        </Box>
                    ))}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setHistoryItem(null)}>Fechar</Button>
                </DialogActions>
            </Dialog>
        </>
    );

    // Shell mobile: detalhe do protótipo `inventario/detalhe.html` (mesma contagem, scan e ações).
    if (isMobile) {
        return (
            <PageScaffold
                links={[{ title: 'Inventário de Estoque', link: '/inventory' }]}
                titlePageTarget={inventory ? `Inventário #${inventory.code}` : 'Inventário'}
                openSnackbar={openSnackbar}
                setOpenSnackbar={setOpenSnackbar}
                message={snackbarMessage}
                severity={snackbarSeverity}
            >
                <MobileInventoryDetail
                    loading={loading}
                    inventory={inventory}
                    summary={summary}
                    items={data?.items || []}
                    canEdit={canEdit}
                    canApprove={canApprove}
                    canExport={canExport}
                    canCountItems={canCountItems}
                    edits={edits}
                    setEdits={setEdits}
                    inputRefs={inputRefs}
                    commitCount={commitCount}
                    diffColor={diffColor}
                    openHistory={openHistory}
                    onSaveAll={saveAllEdits}
                    onAction={setConfirmAction}
                    onExport={handleExport}
                    scan={{ inputRef: scanRef, code: scanCode, setCode: setScanCode, onScan: () => handleScan(), onCamera: handleOpenCamera, last: lastScan }}
                />
                {scannerOpen && (
                    <BarcodeScanner
                        onClose={() => setScannerOpen(false)}
                        onError={(message) => notify(message, 'error')}
                        continuous
                        onDetected={(code) => handleScan(code)}
                        feedback={scanFeedback && (
                            <Box>
                                <Typography sx={{ fontSize: '12px', opacity: 0.7 }}>Código: {scanFeedback.code}</Typography>
                                {scanFeedback.type === 'success' ? (
                                    <>
                                        <Typography sx={{ fontSize: '15px', fontWeight: 700 }}>{scanFeedback.name}</Typography>
                                        <Typography sx={{ fontSize: '18px', fontWeight: 800, color: '#66bb6a' }}>Contagem atual: {scanFeedback.qty}</Typography>
                                    </>
                                ) : (
                                    <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#ef5350' }}>{scanFeedback.message}</Typography>
                                )}
                            </Box>
                        )}
                    />
                )}
                {dialogs}
            </PageScaffold>
        );
    }

    return (
        <PageScaffold
            links={[{ title: 'Inventário de Estoque', link: '/inventory' }]}
            titlePageTarget={inventory ? `Inventário #${inventory.code}` : 'Inventário'}
            openSnackbar={openSnackbar}
            setOpenSnackbar={setOpenSnackbar}
            message={snackbarMessage}
            severity={snackbarSeverity}
        >
            <Box sx={{ width: '100%', maxWidth: 1400, mx: 'auto' }}>
                {inventory && (
                    <>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: isMobile ? 1 : 2, mb: isMobile ? 1 : 2 }}>
                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <Typography component="h1" sx={{ fontSize: '22px', fontWeight: 800, letterSpacing: '-0.02em' }}>
                                        Inventário #{inventory.code}
                                    </Typography>
                                    <Chip label={statusInfo.label} size="small" sx={{ bgcolor: statusInfo.bg, color: statusInfo.color, fontWeight: 700 }} />
                                    <Chip label={inventory.scope === 'partial' ? 'Parcial' : 'Completo'} size="small" sx={{ bgcolor: 'background.subtle', color: 'text.secondary' }} />
                                </Box>
                                <Box
                                    ref={extraRef}
                                    sx={{
                                        overflow: 'hidden',
                                        height: !canCollapseSummary || extraHeight === null ? 'auto' : `${extraHeight}px`,
                                        transition: summaryDragging ? 'none' : 'height 0.2s ease',
                                    }}
                                >
                                    <Typography sx={{ fontSize: '13.5px', color: 'text.secondary', mt: 0.5 }}>
                                        {inventory.description || 'Sem descrição'}
                                        {inventory.location_label ? ` · Local: ${inventory.location_label}` : ''}
                                    </Typography>
                                    <Typography sx={{ fontSize: '12px', color: 'text.disabled', mt: 0.5 }}>
                                        Criado por {inventory.created_by || '—'} em {formatDateTime(inventory.created_at)}
                                        {inventory.started_at ? ` · Iniciado por ${inventory.started_by || '—'} em ${formatDateTime(inventory.started_at)}` : ''}
                                        {inventory.finished_at ? ` · Finalizado por ${inventory.finished_by || '—'} em ${formatDateTime(inventory.finished_at)}` : ''}
                                    </Typography>
                                </Box>
                            </Box>

                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: isMobile ? 0.75 : 1,
                                    flexWrap: 'wrap',
                                    ...(isMobile && {
                                        '& .MuiButton-root': {
                                            height: 34,
                                            minHeight: 34,
                                            minWidth: 0,
                                            px: 1.25,
                                            py: 0,
                                            fontSize: '12px',
                                            lineHeight: 1.2,
                                            '& .MuiButton-startIcon': { mr: 0.5, ml: 0 },
                                        },
                                    }),
                                }}
                            >
                                <Button variant="outlined" size="small" startIcon={<ArrowLeft size={14} />} onClick={() => navigate('/inventory')}>
                                    Voltar
                                </Button>
                                {canExport && (
                                    <>
                                        <Button variant="outlined" size="small" startIcon={<Download size={14} />} onClick={() => handleExport('csv')}>
                                            Excel (CSV)
                                        </Button>
                                        <Button variant="outlined" size="small" startIcon={<Download size={14} />} onClick={() => handleExport('pdf')}>
                                            PDF
                                        </Button>
                                    </>
                                )}
                                {canEdit && status === 'draft' && (
                                    <Button variant="contained" size="small" onClick={() => setConfirmAction('start')}>Iniciar contagem</Button>
                                )}
                                {canEdit && status === 'counting' && (
                                    <Button variant="contained" size="small" onClick={() => setConfirmAction('submit')}>Enviar para conferência</Button>
                                )}
                                {canApprove && status === 'review' && (
                                    <Button variant="contained" size="small" color="success" onClick={() => setConfirmAction('approve')}>
                                        Aprovar e ajustar estoque
                                    </Button>
                                )}
                                {canEdit && ['draft', 'counting', 'review'].includes(status) && (
                                    <Button variant="outlined" size="small" color="error" onClick={() => setConfirmAction('cancel')}>Cancelar inventário</Button>
                                )}
                            </Box>
                        </Box>

                        {summary && (
                            <Box
                                ref={summaryRef}
                                sx={{
                                    overflow: 'hidden',
                                    height: !canCollapseSummary || summaryHeight === null ? 'auto' : `${summaryHeight}px`,
                                    transition: summaryDragging ? 'none' : 'height 0.2s ease',
                                }}
                            >
                                <Box sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(4, minmax(0, 1fr))', xl: 'repeat(8, minmax(0, 1fr))' },
                                    gap: 1,
                                    mb: canCollapseSummary ? 0.5 : 2,
                                }}>
                                    <SummaryCard label="Total de produtos" value={summary.total_products} />
                                    <SummaryCard label="Contados" value={summary.counted} />
                                    <SummaryCard label="Sem divergência" value={summary.no_divergence} />
                                    <SummaryCard label="Com falta" value={`${summary.shortage} (${summary.shortage_qty} un.)`} color="error.main" />
                                    <SummaryCard label="Com sobra" value={`${summary.surplus} (${summary.surplus_qty} un.)`} color="success.main" />
                                    <SummaryCard label="Qtd. ajustada" value={summary.adjusted_qty} />
                                    <SummaryCard label="Perdas (R$)" value={summary.loss_value} color="error.main" />
                                    <SummaryCard label="Sobras (R$)" value={summary.gain_value} color="success.main" />
                                </Box>
                            </Box>
                        )}

                        {canCollapseSummary && (
                            <Box
                                role="separator"
                                aria-orientation="horizontal"
                                aria-label="Arraste para recolher ou expandir os indicadores"
                                onPointerDown={handleSummaryDragStart}
                                onPointerMove={handleSummaryDragMove}
                                onPointerUp={handleSummaryDragEnd}
                                onPointerCancel={handleSummaryDragEnd}
                                sx={{
                                    touchAction: 'none',
                                    userSelect: 'none',
                                    cursor: 'row-resize',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    height: 24,
                                    mb: 0.5,
                                }}
                            >
                                <Box sx={{ width: 44, height: 5, borderRadius: '3px', bgcolor: 'divider' }} />
                            </Box>
                        )}

                        {isCounting && canEdit && (
                            <Box
                                sx={{
                                    border: 1,
                                    borderColor: 'divider',
                                    borderRadius: '12px',
                                    p: isMobile ? 1 : 2,
                                    mb: isMobile ? 1 : 2,
                                    bgcolor: 'background.paper',
                                    ...(isMobile && { position: 'sticky', top: APP_BAR_OFFSET, zIndex: 5 }),
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: isMobile ? 1 : 1.5, flexWrap: isMobile ? 'nowrap' : 'wrap' }}>
                                    {!isMobile && <ScanLine size={20} />}
                                    <TextField
                                        inputRef={scanRef}
                                        autoFocus
                                        label={isMobile ? 'Código do produto' : 'Código do produto (escaneie e pressione Enter)'}
                                        value={scanCode}
                                        onChange={(e) => setScanCode(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleScan(); } }}
                                        sx={{ ...standaloneFieldSx, flex: isMobile ? '1 1 0' : '1 1 320px', minWidth: 0, '& .MuiInputLabel-root': { ...standaloneFieldSx['& .MuiInputLabel-root'], maxWidth: 'calc(100% - 28px)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }}
                                    />
                                    {isMobile && (
                                        <Button
                                            variant="outlined"
                                            aria-label="Ler com a câmera"
                                            title="Ler com a câmera"
                                            onClick={handleOpenCamera}
                                            sx={{ height: 52, width: 52, minWidth: 52, p: 0, borderRadius: '12px', flexShrink: 0 }}
                                        >
                                            <Camera size={20} />
                                        </Button>
                                    )}
                                </Box>
                                {scannerOpen && (
                                    <BarcodeScanner
                                        onClose={() => setScannerOpen(false)}
                                        onError={(message) => notify(message, 'error')}
                                        continuous
                                        onDetected={(code) => handleScan(code)}
                                        feedback={scanFeedback && (
                                            <Box>
                                                <Typography sx={{ fontSize: '12px', opacity: 0.7 }}>Código: {scanFeedback.code}</Typography>
                                                {scanFeedback.type === 'success' ? (
                                                    <>
                                                        <Typography sx={{ fontSize: '15px', fontWeight: 700 }}>{scanFeedback.name}</Typography>
                                                        <Typography sx={{ fontSize: '18px', fontWeight: 800, color: '#66bb6a' }}>Contagem atual: {scanFeedback.qty}</Typography>
                                                    </>
                                                ) : (
                                                    <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#ef5350' }}>{scanFeedback.message}</Typography>
                                                )}
                                            </Box>
                                        )}
                                    />
                                )}
                                {lastScan && (
                                    <Box sx={{ mt: 1.5 }}>
                                        <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>Código: {lastScan.code}</Typography>
                                        <Typography sx={{ fontSize: '15px', fontWeight: 700 }}>{lastScan.name}</Typography>
                                        <Typography sx={{ fontSize: '20px', fontWeight: 800, color: 'primary.main' }}>
                                            Contagem atual: {lastScan.counted_qty}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        )}

                        {!isMobile && (status === 'review' || status === 'completed') && (
                            <FormControlLabel
                                sx={{ m: 0, mb: 1, height: 40 }}
                                control={<Switch checked={onlyDivergent} onChange={(e) => setOnlyDivergent(e.target.checked)} />}
                                label={<Typography sx={{ fontSize: '13px' }}>Revisar somente divergentes</Typography>}
                            />
                        )}
                    </>
                )}

                {isMobile ? (
                    <MobileItemList
                        loading={loading}
                        allItems={data?.items || []}
                        canCountItems={canCountItems}
                        edits={edits}
                        setEdits={setEdits}
                        inputRefs={inputRefs}
                        commitCount={commitCount}
                        diffColor={diffColor}
                        openHistory={openHistory}
                    />
                ) : (
                    <DefaultTable
                        colunms={columns}
                        data={items}
                        iconsActions={tableActions}
                        loading={loading}
                        rowPerPage={15}
                        rowsPerPageOptions={[15, 30, 50]}
                        itemLabel="produtos"
                        sortableFields={['code', 'name', 'difference']}
                        defaultSort={{ field: 'name', direction: 'asc' }}
                    />
                )}
            </Box>

            {dialogs}
        </PageScaffold>
    );
}
